'use client';

import type { ReactNode } from 'react';
import { TrendingUp } from 'lucide-react';
import { ADAPTER_KIND_LABEL, type LobbyAdapter } from '../../data/mock-adapters';
import type { V2GridAsset } from '@/lib/types/v2';
import { FlaunchAdapterPanel } from './FlaunchAdapterPanel';
import { UniswapV3AdapterPanel } from './UniswapV3AdapterPanel';
import { UniswapV4AdapterPanel } from './UniswapV4AdapterPanel';
import { AerodromeAdapterPanel } from './AerodromeAdapterPanel';
import { CustomAdapterPanel } from './CustomAdapterPanel';
import { CreatorWithdrawLpButton } from './CreatorWithdrawLpButton';
import { BoardroomProtocolCollectFees } from './BoardroomProtocolCollectFees';
import { BoardroomKeeperCollectFees } from './BoardroomKeeperCollectFees';
import {
  HV_TAKEOVER_ADAPTER_ADDRESS,
  HV_KEEPER_FEE_ADAPTER_ADDRESS,
  HV_KEEPER_FEE_ADAPTER_ASSET_HASH,
} from '@/lib/constants/addresses';
import { HV_PROTOCOL_ADAPTER_ASSET_HASH } from '@/hooks/use-boardroom-protocol-fees';

export interface CreatorWithdrawLpContext {
  /** From {@link useGridPage}: viewer holds seats `0 … totalSeats-1`. */
  ownsAllTiles: boolean;
  viewerAddress: string | null | undefined;
  liveGridCompositeId: string;
  assets: V2GridAsset[];
}

function withdrawLpSlotForAdapter(
  adapter: LobbyAdapter,
  ctx: CreatorWithdrawLpContext | undefined,
): ReactNode | undefined {
  if (!ctx) return undefined;
  if (
    adapter.kind !== 'flaunch' &&
    adapter.kind !== 'uniswap-v3' &&
    adapter.kind !== 'uniswap-v4'
  ) {
    return undefined;
  }
  const asset = ctx.assets.find((a) => a.id === adapter.id);
  if (!asset?.adapterAddress || !asset.active || asset.withdrawnAt) return undefined;
  return (
    <CreatorWithdrawLpButton
      assetHash={asset.id as `0x${string}`}
      adapterAddress={asset.adapterAddress as `0x${string}`}
      ownsAllTiles={ctx.ownsAllTiles}
      viewerAddress={ctx.viewerAddress}
      liveGridCompositeId={ctx.liveGridCompositeId}
    />
  );
}

/** Collect taxes chip on the HV protocol custom-yield card (homepage boardroom). */
function boardroomProtocolFeesBelowStats(
  adapter: LobbyAdapter,
  boardroomProtocolFeesEnabled: boolean | undefined,
): ReactNode | undefined {
  if (!boardroomProtocolFeesEnabled) return undefined;
  if (adapter.kind !== 'custom') return undefined;
  const hv = (HV_TAKEOVER_ADAPTER_ADDRESS || '').toLowerCase().trim();
  const contractLc = (adapter.contractAddress || '').toLowerCase();
  const idLc = adapter.id.toLowerCase();
  const canonHashLc = HV_PROTOCOL_ADAPTER_ASSET_HASH.toLowerCase();
  if ((hv && contractLc === hv) || idLc === canonHashLc) {
    return <BoardroomProtocolCollectFees />;
  }
  // Keeper-fee adapter: same shell, different button. Match either the
  // pinned contract address or the registered asset hash so the row is
  // detected before / after the indexer enriches `adapterAddress`.
  const keeper = (HV_KEEPER_FEE_ADAPTER_ADDRESS || '').toLowerCase().trim();
  const keeperHashLc = (HV_KEEPER_FEE_ADAPTER_ASSET_HASH || '').toLowerCase().trim();
  if ((keeper && contractLc === keeper) || (keeperHashLc && idLc === keeperHashLc)) {
    return <BoardroomKeeperCollectFees />;
  }
  return undefined;
}

/**
 * Renders one panel per registered adapter on the active board, stacked
 * vertically. The shell + heading reads as a single "Board Yield" widget,
 * but each adapter renders its own contract-shaped UI underneath:
 *
 *   - FlaunchAdapterPanel      — coin Mcap / 24h vol / accrued
 *   - UniswapV3AdapterPanel    — fee tier / range / current tick
 *   - UniswapV4AdapterPanel    — fee tier / hook / pair
 *   - AerodromeAdapterPanel    — gauge weight / veBoost
 *   - CustomAdapterPanel       — contract name + headline metric
 *
 * Replaces the old single-row YieldPanel that hard-coded a Flaunch / Uni
 * pair. The stack is mounted in the same DraggableFloatingPanel slot the
 * old YieldPanel used, so the existing 'yield' panel id + hotkey continue
 * to toggle the entire adapter column on/off.
 */
export function AdapterStack({
  adapters,
  footer,
  creatorWithdrawLp,
  boardroomProtocolFees,
  loading = false,
}: {
  adapters: LobbyAdapter[];
  /** Optional creator tools / chrome rendered below the adapter cards.
   *  Currently used to stack the full-width "+ Add yield" button and the
   *  collapsible "Edit Panel" for adapter copy. */
  footer?: ReactNode;
  /** Live-board creator: Flaunch / Uniswap LP withdrawal when viewer holds every seat. */
  creatorWithdrawLp?: CreatorWithdrawLpContext | undefined;
  /** Boardroom homepage: show protocol tax collect UI on the HV custom adapter row. */
  boardroomProtocolFees?: boolean | undefined;
  /** Driven by `isBoardDataLoading` in `LobbyClient` — force skeleton
   *  rows while switching boards so the panel never flashes "No adapters
   *  registered" (empty state) or the previous board's adapter list. */
  loading?: boolean;
}) {
  const showSkeleton = loading;
  return (
    <div
      className="rounded-[6px] bg-black/40 border border-amber-500/25 backdrop-blur-md overflow-hidden"
      aria-busy={showSkeleton || undefined}
    >
      {/* Slimmer header: single row, no icon chrome. The surrounding
          DraggableFloatingPanel already signals this is a yield widget
          via the panel id + hotkey, so the `[icon] Board Yield` framing
          was overstated against a 288px column. */}
      <div className="px-2.5 py-1.5 flex items-center gap-2 border-b border-amber-500/15">
        <TrendingUp className="h-3 w-3 text-amber-200 shrink-0" />
        <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-amber-100">
          Board Yield
        </span>
        <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-emerald-300/80 ml-auto tabular-nums">
          {showSkeleton
            ? '…'
            : `${adapters.length} yield source${adapters.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="p-1 space-y-1">
        {showSkeleton ? (
          <>
            <AdapterSkeletonRow />
            <AdapterSkeletonRow />
          </>
        ) : (
          <>
            {adapters.map((adapter) => (
              <AdapterPanel
                key={adapter.id}
                adapter={adapter}
                creatorWithdrawLp={creatorWithdrawLp}
                boardroomProtocolFees={boardroomProtocolFees}
              />
            ))}
            {adapters.length === 0 && (
              <div className="px-3 py-5 text-center text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">
                No adapters registered
              </div>
            )}
          </>
        )}
        {footer ? <div className="pt-1">{footer}</div> : null}
      </div>
    </div>
  );
}

function AdapterSkeletonRow() {
  return (
    <div className="rounded-[4px] border border-amber-500/10 bg-black/30 px-2.5 py-2">
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded-[2px] bg-amber-200/20 animate-pulse" />
        <div className="h-2.5 w-24 rounded bg-amber-200/15 animate-pulse" />
        <div className="ml-auto h-2 w-10 rounded bg-zinc-500/25 animate-pulse" />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <div className="h-2 rounded bg-zinc-500/20 animate-pulse" />
        <div className="h-2 rounded bg-zinc-500/20 animate-pulse" />
        <div className="h-2 rounded bg-zinc-500/20 animate-pulse" />
      </div>
    </div>
  );
}

function AdapterPanel({
  adapter,
  creatorWithdrawLp,
  boardroomProtocolFees,
}: {
  adapter: LobbyAdapter;
  creatorWithdrawLp?: CreatorWithdrawLpContext | undefined;
  boardroomProtocolFees?: boolean | undefined;
}) {
  const lpSlot = withdrawLpSlotForAdapter(adapter, creatorWithdrawLp);
  const boardroomFees = boardroomProtocolFeesBelowStats(adapter, boardroomProtocolFees);
  switch (adapter.kind) {
    case 'flaunch':
      return <FlaunchAdapterPanel adapter={adapter} belowStats={lpSlot} />;
    case 'uniswap-v3':
      return <UniswapV3AdapterPanel adapter={adapter} belowStats={lpSlot} />;
    case 'uniswap-v4':
      return <UniswapV4AdapterPanel adapter={adapter} belowStats={lpSlot} />;
    case 'aerodrome':
      return <AerodromeAdapterPanel adapter={adapter} />;
    case 'custom':
      return <CustomAdapterPanel adapter={adapter} belowStats={boardroomFees} />;
    default: {
      const _exhaustive: never = adapter;
      void _exhaustive;
      return (
        <div className="px-3 py-2 text-[10px] font-mono tracking-[0.18em] uppercase text-amber-300/80">
          Unknown adapter · {ADAPTER_KIND_LABEL[(adapter as LobbyAdapter).kind] ?? '???'}
        </div>
      );
    }
  }
}
